import clsx from 'clsx'
import { AlertTriangle, FileText, MessageSquare } from 'lucide-react'
import type { DossierPipelineStage } from './types'

export function PipelineStepper({
  stages,
  selectedId,
  onStageClick,
  totalAgeDays,
  targetClose,
}: {
  stages: DossierPipelineStage[]
  selectedId?: string | null
  onStageClick?: (stage: DossierPipelineStage) => void
  totalAgeDays?: number | null
  targetClose?: string | null
}) {
  return (
    <div className="flex items-stretch gap-2">
      <div className="min-w-0 flex-1 overflow-x-auto">
        <ol className="flex min-w-max items-stretch gap-0.5">
          {stages.map((stage, index) => {
            const selected = selectedId === stage.id
            const interactive = Boolean(onStageClick) && stage.status !== 'upcoming'
            const shellClass = clsx(
              'flex w-full min-w-[100px] flex-1 flex-col text-left transition',
              interactive && 'cursor-pointer hover:brightness-110',
              !interactive && stage.status === 'upcoming' && 'cursor-default opacity-70',
            )

            const body = (
              <>
                <div
                  className={clsx(
                    'flex items-center gap-1 border px-1.5 py-1',
                    selected || stage.status === 'current'
                      ? 'border-amber-400/55 bg-amber-400/12'
                      : stage.status === 'completed'
                        ? 'border-emerald-500/30 bg-emerald-500/[0.06]'
                        : 'border-stone-800/50 bg-black/20',
                  )}
                >
                  <span
                    className={clsx(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold',
                      selected || stage.status === 'current'
                        ? 'bg-amber-400/30 text-amber-100'
                        : stage.status === 'completed'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-stone-900 text-stone-600',
                    )}
                  >
                    {stage.status === 'completed' ? '✓' : index + 1}
                  </span>
                  <span
                    className={clsx(
                      'truncate text-[10px] font-semibold',
                      selected || stage.status === 'current' ? 'text-amber-100' : 'text-stone-300',
                    )}
                  >
                    {stage.label}
                  </span>
                </div>
                <div
                  className={clsx(
                    'border-x border-b px-1.5 py-1',
                    selected ? 'border-amber-400/40 bg-amber-400/[0.06]' : 'border-stone-800/50 bg-black/30',
                  )}
                >
                  <div className="truncate whitespace-nowrap text-center font-mono text-[9px] text-amber-200/80">
                    {stage.days != null
                      ? `${stage.days}d in stage`
                      : stage.status === 'upcoming'
                        ? 'Not entered'
                        : '—'}
                  </div>
                  <div className="truncate whitespace-nowrap text-center text-[8px] text-stone-500">
                    {stage.dateRange ?? '—'}
                  </div>
                  {stage.owner ? (
                    <div className="truncate whitespace-nowrap text-center text-[8px] text-stone-500">
                      {stage.owner}
                    </div>
                  ) : null}
                  <div className="mt-0.5 flex items-center justify-center gap-1.5 text-stone-600">
                    {stage.docCount != null ? (
                      <span className="inline-flex items-center gap-0.5 text-[8px]">
                        <FileText size={8} /> {stage.docCount}
                      </span>
                    ) : null}
                    {stage.commentCount != null ? (
                      <span className="inline-flex items-center gap-0.5 text-[8px]">
                        <MessageSquare size={8} /> {stage.commentCount}
                      </span>
                    ) : null}
                    {stage.alertCount != null && stage.alertCount > 0 ? (
                      <span className="inline-flex items-center gap-0.5 text-[8px] text-red-400/90">
                        <AlertTriangle size={8} /> {stage.alertCount}
                      </span>
                    ) : null}
                  </div>
                </div>
              </>
            )

            return (
              <li key={stage.id} className="flex min-w-[100px] flex-1 flex-col">
                {interactive ? (
                  <button
                    type="button"
                    className={shellClass}
                    onClick={() => onStageClick?.(stage)}
                    title={`View activity during ${stage.label}`}
                  >
                    {body}
                  </button>
                ) : (
                  <div className={shellClass}>{body}</div>
                )}
              </li>
            )
          })}
        </ol>
      </div>

      <div className="flex w-[118px] shrink-0 flex-col justify-center rounded border border-amber-400/25 bg-amber-400/[0.06] px-2.5 py-2 text-center">
        <div
          className="font-mono text-[8px] font-semibold uppercase tracking-[0.14em] text-amber-300/90"
          title="Counted from when the record entered the system, not from when the package was received"
        >
          Total Age
        </div>
        <div className="mt-0.5 text-[18px] font-semibold leading-none text-stone-50">
          {totalAgeDays != null ? totalAgeDays : '—'}
          {totalAgeDays != null ? (
            <span className="ml-1 text-[10px] font-normal text-stone-400">days</span>
          ) : null}
        </div>
        <div className="mt-0.5 text-[8px] text-stone-500">
          {totalAgeDays != null ? 'since entry' : null}
        </div>
        <div className="mt-2 border-t border-amber-400/15 pt-1.5">
          <div className="font-mono text-[8px] font-semibold uppercase tracking-[0.14em] text-amber-300/90">
            Target Close
          </div>
          <div className="mt-0.5 text-[11px] font-medium text-stone-200">{targetClose ?? '—'}</div>
        </div>
      </div>
    </div>
  )
}
